export type PoolOptions = {
  concurrency: number;
  signal?: AbortSignal;
};

export async function runPoolSettled<TJob, TResult>(
  jobs: readonly TJob[],
  worker: (job: TJob, jobIndex: number) => Promise<TResult>,
  options: PoolOptions
): Promise<PromiseSettledResult<TResult>[]> {
  const concurrency = Math.max(1, Math.floor(options.concurrency || 1));
  const results: PromiseSettledResult<TResult>[] = new Array(jobs.length);

  if (jobs.length === 0) return results;

  let nextIndex = 0;
  let active = 0;

  return await new Promise(resolve => {
    const schedule = () => {
      if (options.signal?.aborted) {
        for (let i = nextIndex; i < jobs.length; i += 1) {
          results[i] = {
            status: 'rejected',
            reason: new DOMException('Aborted', 'AbortError'),
          };
        }
        if (active === 0) resolve(results);
        return;
      }

      while (active < concurrency && nextIndex < jobs.length) {
        const idx = nextIndex;
        nextIndex += 1;
        active += 1;

        void (async () => {
          try {
            const value = await worker(jobs[idx], idx);
            results[idx] = { status: 'fulfilled', value };
          } catch (error) {
            results[idx] = { status: 'rejected', reason: error };
          } finally {
            active -= 1;

            if (nextIndex >= jobs.length && active === 0) {
              resolve(results);
            } else {
              schedule();
            }
          }
        })();
      }
    };

    schedule();
  });
}

